// MOTOR DE PARTIDA -- simulacao posse a posse.
//
// ============================================================================
// A decisao de arquitetura mais importante do projeto inteiro esta aqui.
//
// Num jogo de futebol, gol e evento raro: da ~2,7 por partida. Por isso o
// projeto anterior tinha DOIS motores -- um que decidia o placar e outro que
// montava a narracao DE TRAS PARA FRENTE a partir do placar ja decidido. Duas
// verdades sobre o mesmo jogo, e todo bug de narracao virava caca ao fantasma.
//
// Basquete nao precisa disso. Sao ~200 posses por partida: granularidade
// suficiente para simular PARA A FRENTE e deixar o placar emergir. O log de
// posses JA E a sumula, e a sumula JA E a narracao. Um motor, uma verdade:
//   - "Simular"  -> roda com playByPlay: false (sem alocar texto)
//   - "Assistir" -> roda com playByPlay: true e a UI consome os eventos
// O resultado e identico nos dois casos para a mesma semente.
// ============================================================================

import { makeRng, clamp } from './rng.js';
import {
  AVG, CLOCK, RATES, ROTATION, HOME_COURT, SCORE_EFFECT, CLUTCH, resolveTactics,
} from './tuning.js';
import { makeStatLine, teamTotals } from './boxscore.js';
import { pickLineup } from './rotation.js';

/** Converte diferenca de atributo em ajuste de probabilidade. */
const adj = (attr, slope) => (attr - AVG) * slope;

/** Duracao de posse sempre valida: NaN aqui congela a partida inteira. */
const safeSeconds = (v) => (Number.isFinite(v) ? clamp(v, 6, 24) : CLOCK.secondsPerPossession);

function prepareSide(team, isHome) {
  const squad = team.roster;
  return {
    team,
    isHome,
    tac: resolveTactics(team.tactics),
    squad,
    lines: squad.map((p) => makeStatLine({ playerId: p.id, name: p.name, position: p.position })),
    state: squad.map(() => ({ seconds: 0, fouls: 0, available: true })),
    onCourt: [],
    w: null,
    score: 0,
    possessions: 0,
    quarterScores: [],
  };
}

/**
 * Precomputa os pesos de sorteio dos 5 em quadra. Chamado somente quando a
 * escalacao muda -- nunca dentro do laco de posse.
 */
function refreshWeights(side) {
  const n = side.onCourt.length;
  const w = {
    usage: new Array(n),
    steal: new Array(n),
    block: new Array(n),
    foul: new Array(n),
    rebOff: new Array(n),
    rebDef: new Array(n),
    passing: new Array(n),
  };
  let defense = 0;
  let rebOff = 0;
  let rebDef = 0;
  for (let k = 0; k < n; k++) {
    const a = side.squad[side.onCourt[k]].attributes;
    // Peso de sorteio: elevar a diferenca ao quadrado faz o astro terminar
    // muito mais posses que o decimo homem, como acontece de verdade. O
    // expoente sobe com "na mao do astro" e cai com "dividir a bola".
    w.usage[k] = Math.pow(Math.max(a.usage - 35, 1), 1.9 + (side.tac?.focus.starPower ?? 0));
    w.steal[k] = Math.max(a.steal - 30, 1);
    w.block[k] = Math.max(a.block - 30, 1);
    // Expoente abaixo de 1 ACHATA a distribuicao: quem falta mais continua
    // faltando mais, mas nao concentra a ponto de sair de quadra em 70% dos
    // jogos. Na NBA um atleta chega as 6 faltas em ~0,3 jogos.
    w.foul[k] = Math.pow(Math.max(a.foulProne - 30, 1), 0.62);
    w.rebOff[k] = Math.max(a.rebOff - 30, 1);
    w.rebDef[k] = Math.max(a.rebDef - 30, 1);
    w.passing[k] = Math.max(a.passing - 30, 1);
    defense += a.defense;
    rebOff += a.rebOff;
    rebDef += a.rebDef;
  }
  w.teamDefense = defense / n;
  w.teamRebOff = rebOff / n;
  w.teamRebDef = rebDef / n;
  side.w = w;
}

function setLineup(side, elapsedFraction, bestBias) {
  const next = pickLineup(side.squad, side.state, elapsedFraction, bestBias);
  side.onCourt = next;
  refreshWeights(side);
}

/**
 * Marca uma falta e tira o atleta se ele chegou ao limite.
 *
 * A saida acontece AQUI, no momento da falta -- nao depois da posse. Numa mesma
 * posse cabem duas faltas do mesmo defensor (uma sem arremesso e outra no
 * arremesso), e foi assim que apareceram atletas com 7 e 8 faltas na sumula.
 */
function marcarFalta(side, idx) {
  side.lines[idx].pf += 1;
  side.state[idx].fouls += 1;
  if (side.state[idx].fouls >= ROTATION.maxFouls) enforceFoulOuts(side);
}

/** Ajusta o placar e o saldo (+/-) dos 10 atletas em quadra. */
function addPoints(off, def, points) {
  off.score += points;
  for (const i of off.onCourt) off.lines[i].plusMinus += points;
  for (const i of def.onCourt) def.lines[i].plusMinus -= points;
}

/**
 * Uma posse. Devolve os segundos consumidos.
 * A posse so termina quando a bola troca de mao: rebote ofensivo continua a
 * MESMA posse (e por isso que rebote ofensivo nao infla o ritmo do jogo).
 */
function playPossession(off, def, rng, log, ctx) {
  off.possessions += 1;
  const homeShot = off.isHome ? HOME_COURT.shootingBonus : 0;
  const homeTov = off.isHome ? HOME_COURT.turnoverBonus : 0;
  // Sistema de jogo: o do ATAQUE e o da DEFESA se encontram na mesma posse.
  const ata = off.tac;
  const dfe = def.tac;

  let seconds = clamp(
    Math.round(ctx.secondsPerPossession + rng.normal() * CLOCK.possessionSpread),
    CLOCK.minPossessionSeconds,
    CLOCK.maxPossessionSeconds,
  );

  // Cada jogada carrega o PLACAR do momento: e o que permite a tela reproduzir
  // a partida sem recalcular nada, e garante que o placar mostrado ao vivo e
  // exatamente o que a sumula vai dizer no fim.
  const push = log
    ? (type, text, extra) => log.push({
      q: ctx.quarter,
      clock: Math.max(0, ctx.clock - seconds),
      side: off.isHome ? 'home' : 'away',
      type,
      text,
      hs: ctx.home.score,
      as: ctx.away.score,
      ...extra,
    })
    : null;

  for (let chain = 0; chain < 8; chain++) {
    // ---------------------------------------------------------- quem tem a bola
    const k = rng.weighted(off.w.usage);
    const oi = off.onCourt[k];
    const shooter = off.squad[oi];
    const sa = shooter.attributes;
    const oLine = off.lines[oi];

    // ------------------------------------------------ falta proposital
    // Ultimos segundos, o time que ESTA DEFENDENDO perde por pouco: para o
    // cronometro mandando o adversario para a linha.
    if (chain === 0 && ctx.lastPeriod && ctx.clock <= CLUTCH.intentionalFoulSeconds
        && off.score - def.score > 0 && off.score - def.score <= CLUTCH.intentionalFoulLead) {
      const fk = rng.weighted(def.w.foul);
      const fi = def.onCourt[fk];
      marcarFalta(def, fi);
      const pFt = clamp(RATES.freeThrow + adj(sa.freeThrow, RATES.freeThrowPerAttr), 0.35, 0.98);
      for (let s2 = 0; s2 < 2; s2++) {
        oLine.fta += 1;
        if (rng.chance(pFt)) {
          oLine.ftm += 1;
          oLine.pts += 1;
          addPoints(off, def, 1);
        }
      }
      if (push) push('intentionalFoul', `Falta proposital de ${def.squad[fi].name}`, { playerId: shooter.id });
      return Math.min(seconds, 4);
    }

    // ------------------------------------------- falta de nao-arremesso
    // Nao termina a posse: o ataque repoe a bola. Existe para a contagem de
    // faltas pessoais bater com a realidade (e para o foul-out ser possivel).
    if (chain === 0 && rng.chance(RATES.nonShootingFoul + dfe.defense.foulRate)) {
      const fk = rng.weighted(def.w.foul);
      const fi = def.onCourt[fk];
      marcarFalta(def, fi);
      if (push) push('foul', `Falta de ${def.squad[fi].name}`, { playerId: def.squad[fi].id });
    }

    // ------------------------------------------------------------- turnover
    const pTov = RATES.turnover
      + adj(sa.handle, RATES.turnoverPerHandle)
      + adj(def.w.teamDefense, RATES.turnoverPerDefense)
      + homeTov + ata.pace.tov + dfe.defense.tovForced;
    // Turnover so na primeira jogada da posse: apos rebote ofensivo o que vem
    // e um putback, nao uma nova armacao.
    if (chain === 0 && rng.chance(pTov)) {
      oLine.tov += 1;
      if (rng.chance(RATES.stealShareOfTurnovers)) {
        const dk = rng.weighted(def.w.steal);
        const di = def.onCourt[dk];
        def.lines[di].stl += 1;
        if (push) push('steal', `${def.squad[di].name} rouba a bola de ${shooter.name}`, { playerId: def.squad[di].id });
      } else {
        // Parte dos turnovers e falta de ataque -- falta pessoal do atacante.
        if (rng.chance(RATES.offensiveFoulShareOfTurnovers)) {
          marcarFalta(off, oi);
          if (push) push('offFoul', `Falta de ataque de ${shooter.name}`, { playerId: shooter.id });
        } else if (push) {
          push('turnover', `${shooter.name} perde a bola`, { playerId: shooter.id });
        }
      }
      return seconds;
    }

    // -------------------------------------------------- falta de arremesso
    if (rng.chance(RATES.shootingFoul + ata.offense.foul + dfe.defense.foulRate * 0.5)) {
      const dk = rng.weighted(def.w.foul);
      const di = def.onCourt[dk];
      marcarFalta(def, di);
      // Falta em tentativa de 3 rende 3 lances.
      const shots = rng.chance(RATES.foulOnThreeShare) ? 3 : 2;
      const pFt = clamp(RATES.freeThrow + adj(sa.freeThrow, RATES.freeThrowPerAttr), 0.35, 0.98);
      let made = 0;
      let lastMissed = false;
      for (let s = 0; s < shots; s++) {
        oLine.fta += 1;
        if (rng.chance(pFt)) {
          oLine.ftm += 1;
          oLine.pts += 1;
          made += 1;
          addPoints(off, def, 1);
          lastMissed = false;
        } else {
          lastMissed = true;
        }
      }
      if (push) push('foulShots', `Falta de ${def.squad[di].name}. ${shooter.name} converte ${made} de ${shots} na linha`, { playerId: shooter.id, points: made });
      // So o ULTIMO lance errado devolve a bola a disputa de rebote.
      if (lastMissed && rng.chance(RATES.offRebound * RATES.ftReboundFactor)) {
        const rk = rng.weighted(off.w.rebOff);
        const ri = off.onCourt[rk];
        off.lines[ri].orb += 1;
        seconds += Math.round(rng.range(CLOCK.offReboundSeconds[0], CLOCK.offReboundSeconds[1]));
        if (push) push('orb', `${off.squad[ri].name} pega o rebote no lance livre`, { playerId: off.squad[ri].id });
        continue; // mesma posse
      }
      if (lastMissed) {
        const rk = rng.weighted(def.w.rebDef);
        const ri = def.onCourt[rk];
        def.lines[ri].drb += 1;
      }
      return seconds;
    }

    // ------------------------------------------------------------ arremesso
    // Contexto de placar: quem esta muito a frente administra, quem esta atras
    // aperta. Comprime as margens extremas sem tocar na media de pontos.
    const lead = clamp(off.score - def.score, -SCORE_EFFECT.maxLead, SCORE_EFFECT.maxLead);
    const leadAdj = -lead * SCORE_EFFECT.perPoint;

    // Ultima posse perdendo por ate 3: procura a bola de 3 para empatar.
    const trailingBy = def.score - off.score;
    const chasing = ctx.lastPeriod && ctx.clock <= CLUTCH.seconds && trailingBy > 0 && trailingBy <= 3;
    const isThree = chasing
      ? rng.chance(CLUTCH.chaseThreeRate)
      : rng.chance(clamp(
        RATES.threeRate + adj(sa.threeTend, RATES.threeRatePerAttr) + ata.offense.threeRate,
        0.02, 0.92,
      ));
    const pMake = isThree
      ? RATES.pctThree + adj(sa.threeShot, RATES.pctThreePerAttr)
        + adj(def.w.teamDefense, RATES.defenseOnShooting) + homeShot + leadAdj
        + (ata.offense.pctThree ?? 0) + dfe.defense.pctThreeAgainst
      : RATES.pctTwo + adj(sa.twoShot, RATES.pctTwoPerAttr)
        + adj(def.w.teamDefense, RATES.defenseOnShooting) + homeShot + leadAdj
        + (ata.offense.pctTwo ?? 0) + dfe.defense.pctTwoAgainst;

    oLine.fga += 1;
    if (isThree) oLine.tpa += 1;

    if (rng.chance(clamp(pMake, 0.15, 0.85))) {
      const points = isThree ? 3 : 2;
      oLine.fgm += 1;
      if (isThree) oLine.tpm += 1;
      oLine.pts += points;
      addPoints(off, def, points);

      // Assistencia: quem passou. Nunca o proprio arremessador.
      const pAssist = (isThree ? RATES.assistOnThree : RATES.assistOnTwo) + ata.focus.assist;
      let assistName = null;
      if (rng.chance(pAssist)) {
        const weights = off.w.passing.map((v, idx) => (idx === k ? 0 : v));
        const ak = rng.weighted(weights);
        const ai = off.onCourt[ak];
        off.lines[ai].ast += 1;
        assistName = off.squad[ai].name;
      }
      if (push) {
        push('made', assistName
          ? `${shooter.name} converte de ${points} (assistencia de ${assistName})`
          : `${shooter.name} converte de ${points}`, { playerId: shooter.id, points });
      }

      // Cesta + falta: um lance livre extra.
      if (rng.chance(RATES.andOne)) {
        const dk = rng.weighted(def.w.foul);
        const di = def.onCourt[dk];
        marcarFalta(def, di);
        const pFt = clamp(RATES.freeThrow + adj(sa.freeThrow, RATES.freeThrowPerAttr), 0.35, 0.98);
        oLine.fta += 1;
        if (rng.chance(pFt)) {
          oLine.ftm += 1;
          oLine.pts += 1;
          addPoints(off, def, 1);
        }
      }
      return seconds;
    }

    // ---------------------------------------------------------- erro + rebote
    const pBlock = isThree ? RATES.blockShareOfMissedThrees : RATES.blockShareOfMissedTwos;
    if (rng.chance(pBlock)) {
      const dk = rng.weighted(def.w.block);
      const di = def.onCourt[dk];
      def.lines[di].blk += 1;
      if (push) push('block', `${def.squad[di].name} bloqueia o arremesso de ${shooter.name}`, { playerId: def.squad[di].id });
    } else if (push) {
      push('miss', `${shooter.name} erra de ${isThree ? 3 : 2}`, { playerId: shooter.id });
    }

    // Nem todo erro gera rebote creditado: bola fora, tap-out para fora, air
    // ball. Sem isso o total de rebotes da liga fica ~8% acima do real.
    if (rng.chance(RATES.reboundLossRate)) return seconds;

    const pOrb = clamp(
      RATES.offRebound
      + adj(off.w.teamRebOff, RATES.offReboundPerAttr)
      - adj(def.w.teamRebDef, RATES.offReboundPerAttr)
      + ata.offense.orb - dfe.defense.drb,
      0.08, 0.55,
    );
    if (rng.chance(pOrb)) {
      const rk = rng.weighted(off.w.rebOff);
      const ri = off.onCourt[rk];
      off.lines[ri].orb += 1;
      seconds += Math.round(rng.range(CLOCK.offReboundSeconds[0], CLOCK.offReboundSeconds[1]));
      if (push) push('orb', `${off.squad[ri].name} pega o rebote ofensivo`, { playerId: off.squad[ri].id });
      continue; // mesma posse: o cronometro de posse reinicia, o de jogo nao
    }

    const rk = rng.weighted(def.w.rebDef);
    const ri = def.onCourt[rk];
    def.lines[ri].drb += 1;
    if (push) push('drb', `${def.squad[ri].name} domina o rebote`, { playerId: def.squad[ri].id });
    return seconds;
  }
  return seconds;
}

/**
 * Simula uma partida completa.
 *
 * @param {object} o
 * @param {object} o.home time da casa  { id, name, roster, tactics? }
 * @param {object} o.away time visitante
 * @param {string|number} o.seed  mesma semente => mesmo jogo, sempre
 * @param {boolean} o.playByPlay  true gera a narracao evento a evento
 */
// ============================================================================
// PARTIDA RETOMAVEL
//
// Antes, a partida era simulada de uma vez so e a tela apenas reproduzia o log.
// Isso era simples e garantia que "assistir" e "simular" davam o mesmo jogo --
// mas impede o tecnico de substituir NO MEIO do jogo, porque quando a tela
// mostra o segundo quarto o quarto period ja aconteceu.
//
// Agora a partida e uma maquina de estado: `createMatch` monta, `stepMatch`
// avanca ate onde voce mandar, `substitute` troca quem esta em quadra, e
// `matchResult` fecha a sumula. `simulateGame` continua existindo e faz tudo de
// uma vez -- e o caminho dos 14 jogos por rodada que o usuario nao assiste.
//
// A garantia de antes continua valendo: SEM intervencao, a mesma semente
// produz exatamente o mesmo jogo, assistindo ou simulando. Com intervencao o
// jogo muda -- que e justamente o ponto de poder intervir.
// ============================================================================

/** Monta a partida, pronta para o primeiro passo. */
export function createMatch({ home, away, seed = 'jogo', playByPlay = false, rules = null }) {
  const rng = makeRng(`partida|${seed}`);
  const H = prepareSide(home, true);
  const A = prepareSide(away, false);

  const quarterSeconds = rules?.quarterSeconds ?? CLOCK.quarterSeconds;
  const overtimeSeconds = rules?.overtimeSeconds ?? CLOCK.overtimeSeconds;

  const ctx = {
    quarter: 1,
    lastPeriod: false,
    home: H,
    away: A,
    clock: quarterSeconds,
    // Ritmo: o do jogo e a MEDIA dos dois sistemas -- um time so nao decide o
    // andamento da partida, ele puxa metade dele.
    //
    // O Number.isFinite nao e paranoia: `tactics.pace` ja foi um numero e virou
    // texto, e a soma antiga produzia NaN. Com NaN, a duracao da posse vira
    // NaN, o relogio nunca chega a zero e a partida roda para sempre.
    /* O sorteio do ritmo fica GUARDADO, e nao so aplicado.
       Trocar a tatica no intervalo precisa recalcular a duracao da posse -- e
       se o sorteio fosse refeito ali, uma troca de tatica viraria uma segunda
       loteria de ritmo no meio do jogo. O acaso e sorteado uma vez por
       partida, como na vida. */
    paceBase: rules?.secondsPerPossession ?? CLOCK.secondsPerPossession,
    paceNoise: rng.normal() * CLOCK.gamePaceSpread,
    secondsPerPossession: 0,
  };

  ctx.secondsPerPossession = safeSeconds(
    ctx.paceBase + (H.tac.pace.secs + A.tac.pace.secs) / 2 + ctx.paceNoise,
  );

  const match = {
    seed: String(seed),
    rng,
    H,
    A,
    ctx,
    log: playByPlay ? [] : null,
    quarterSeconds,
    overtimeSeconds,
    totalSeconds: CLOCK.quarters * quarterSeconds,
    elapsed: 0,
    sinceDecision: ROTATION.segmentSeconds, // forca a primeira escalacao
    offense: rng.chance(0.5) ? H : A,
    overtimes: 0,
    periodStart: { home: 0, away: 0 },
    done: false,
    homeTeam: home,
    awayTeam: away,
  };

  // Quinteto inicial escolhido pelo tecnico, quando houver.
  applyStarters(H, home);
  applyStarters(A, away);
  return match;
}

/** Poe em quadra o quinteto inicial definido pelo usuario, se ele existir. */
function applyStarters(side, team) {
  const starters = team.lineup?.starters;
  if (!Array.isArray(starters) || starters.length !== 5) return;
  // Guarda por ID, nunca por indice: elenco muda entre as partidas, e indice
  // guardado vira o atleta errado depois de uma venda.
  const idx = starters
    .map((id) => side.squad.findIndex((p) => p.id === id))
    .filter((i) => i >= 0);
  if (idx.length !== 5) return;
  side.onCourt = idx;
  side.manual = true;
  refreshWeights(side);
}

/** Fecha o periodo: registra o placar do quarto. */
function closePeriod(match) {
  match.H.quarterScores.push(match.H.score - match.periodStart.home);
  match.A.quarterScores.push(match.A.score - match.periodStart.away);
}

function openPeriod(match, periodSeconds, isOvertime) {
  match.ctx.clock = periodSeconds;
  match.ctx.lastPeriod = isOvertime || match.ctx.quarter >= CLOCK.quarters;
  match.periodStart = { home: match.H.score, away: match.A.score };
}

/**
 * Avanca a partida.
 *
 * @param {object} match
 * @param {object} opts
 * @param {boolean} opts.all           roda ate o fim
 * @param {boolean} opts.untilPeriodEnd para no fim do quarto atual
 * @param {number}  opts.seconds        avanca no maximo N segundos de jogo
 * @returns {{done:boolean, periodEnded:boolean, quarter:number}}
 */
export function stepMatch(match, { all = false, untilPeriodEnd = false, seconds = 0 } = {}) {
  if (match.done) return { done: true, periodEnded: false, quarter: match.ctx.quarter };
  if (!all && !untilPeriodEnd && seconds <= 0) {
    // Sem all, sem fim de periodo e sem segundos, o laco nao teria criterio de
    // parada. Falhar aqui e melhor que rodar para sempre.
    throw new Error('stepMatch precisa de all, untilPeriodEnd ou seconds > 0');
  }
  const { H, A, ctx, rng } = match;
  let restante = seconds;
  // Trava de seguranca: uma partida tem ~200 posses; 4000 e um laco preso.
  let voltas = 0;

  for (;;) {
    if (++voltas > 4000) {
      finishMatch(match);
      return { done: true, periodEnded: true, quarter: ctx.quarter };
    }
    // Fim do periodo?
    if (ctx.clock <= 0) {
      closePeriod(match);
      const acabouRegulamentar = ctx.quarter >= CLOCK.quarters;
      const empatado = H.score === A.score;
      if (acabouRegulamentar && !empatado) {
        finishMatch(match);
        return { done: true, periodEnded: true, quarter: ctx.quarter };
      }
      // Proximo periodo (quarto ou prorrogacao).
      const isOvertime = acabouRegulamentar;
      if (isOvertime) {
        match.overtimes += 1;
        match.totalSeconds += match.overtimeSeconds;
      }
      ctx.quarter += 1;
      openPeriod(match, isOvertime ? match.overtimeSeconds : match.quarterSeconds, isOvertime);
      if (untilPeriodEnd) return { done: false, periodEnded: true, quarter: ctx.quarter };
    }

    // Decide a escalacao a cada segmento -- respeitando quem esta no comando.
    if (match.sinceDecision >= ROTATION.segmentSeconds) {
      const frac = clamp(match.elapsed / match.totalSeconds, 0, 1);
      const isOvertime = ctx.quarter > CLOCK.quarters;
      const decided = !isOvertime
        && ctx.quarter >= CLOCK.quarters
        && ctx.clock <= SCORE_EFFECT.garbageSeconds
        && Math.abs(H.score - A.score) >= SCORE_EFFECT.garbageLead;
      const bestBias = isOvertime ? 1 : decided ? -1 : 0;
      for (const side of [H, A]) {
        // Com o tecnico no comando, o motor so mexe no que a REGRA obriga:
        // quem chegou ao limite de faltas tem de sair. Fora isso, a escalacao
        // e de quem esta no banco -- travar tudo congelaria o time em quadra,
        // e recalcular tudo ignoraria a decisao do usuario.
        if (side.manual) enforceFoulOuts(side);
        else setLineup(side, frac, bestBias);
      }
      match.sinceDecision = 0;
    }

    const defense = match.offense === H ? A : H;
    const gasto = Math.min(playPossession(match.offense, defense, rng, match.log, ctx), ctx.clock);

    ctx.clock -= gasto;
    match.elapsed += gasto;
    match.sinceDecision += gasto;
    for (const i of H.onCourt) H.state[i].seconds += gasto;
    for (const i of A.onCourt) A.state[i].seconds += gasto;
    match.offense = defense;

    if (!all && seconds > 0) {
      restante -= gasto;
      if (restante <= 0) return { done: false, periodEnded: false, quarter: ctx.quarter };
    }
  }
}

/** Tira de quadra quem atingiu o limite de faltas e chama o substituto. */
function enforceFoulOuts(side) {
  const fora = side.onCourt.filter((i) => side.state[i].fouls >= ROTATION.maxFouls);
  if (!fora.length) return;
  for (const i of fora) {
    const banco = side.squad
      .map((p, idx) => idx)
      .filter((idx) => !side.onCourt.includes(idx)
        && side.state[idx].fouls < ROTATION.maxFouls
        && side.state[idx].available !== false)
      .sort((a, b) => side.squad[b].overall - side.squad[a].overall);
    if (!banco.length) continue;
    side.onCourt = side.onCourt.map((x) => (x === i ? banco[0] : x));
  }
  refreshWeights(side);
}

/**
 * Substituicao no meio do jogo.
 * @returns {{ok:boolean, error?:string, out?:string, in?:string}}
 */
export function substitute(match, which, outId, inId) {
  const side = which === 'home' ? match.H : match.A;
  const outIdx = side.squad.findIndex((p) => p.id === outId);
  const inIdx = side.squad.findIndex((p) => p.id === inId);
  if (outIdx < 0 || inIdx < 0) return { ok: false, error: 'Atleta nao esta no elenco.' };
  if (!side.onCourt.includes(outIdx)) return { ok: false, error: 'Esse atleta ja esta no banco.' };
  if (side.onCourt.includes(inIdx)) return { ok: false, error: 'Esse atleta ja esta em quadra.' };
  if (side.state[inIdx].fouls >= ROTATION.maxFouls) {
    return { ok: false, error: `${side.squad[inIdx].name} esta fora por faltas.` };
  }

  side.onCourt = side.onCourt.map((i) => (i === outIdx ? inIdx : i));
  side.manual = true;
  refreshWeights(side);

  if (match.log) {
    match.log.push({
      q: match.ctx.quarter,
      clock: Math.max(0, match.ctx.clock),
      side: which,
      type: 'sub',
      text: `Substituicao: entra ${side.squad[inIdx].name}, sai ${side.squad[outIdx].name}`,
      hs: match.H.score,
      as: match.A.score,
    });
  }
  return { ok: true, out: side.squad[outIdx].name, in: side.squad[inIdx].name };
}

/** Quem esta em quadra agora, com o que a tela precisa mostrar. */
/**
 * Troca a tatica de um lado com a partida em andamento.
 *
 * O tecnico ajusta no intervalo -- e este e o unico caminho para isso. Mexer
 * em `match.H.tac` na mao pulava o recalculo do ritmo, e o time acelerava no
 * papel sem acelerar no relogio.
 *
 * Devolve o ritmo novo em segundos por posse, para a tela poder mostrar o que
 * mudou de fato.
 */
export function setTactics(match, which, tactics) {
  const side = which === 'home' ? match.H : match.A;
  side.team.tactics = tactics;
  side.tac = resolveTactics(tactics);
  match.ctx.secondsPerPossession = safeSeconds(
    match.ctx.paceBase
    + (match.H.tac.pace.secs + match.A.tac.pace.secs) / 2
    + match.ctx.paceNoise,
  );
  return match.ctx.secondsPerPossession;
}

export function courtState(match, which) {
  const side = which === 'home' ? match.H : match.A;
  const mapa = (idx) => ({
    id: side.squad[idx].id,
    name: side.squad[idx].name,
    position: side.squad[idx].position,
    overall: side.squad[idx].overall,
    fouls: side.state[idx].fouls,
    seconds: side.state[idx].seconds,
    pts: side.lines[idx].pts,
    reb: side.lines[idx].orb + side.lines[idx].drb,
    ast: side.lines[idx].ast,
    fouledOut: side.state[idx].fouls >= ROTATION.maxFouls,
  });
  return {
    onCourt: side.onCourt.map(mapa),
    bench: side.squad
      .map((_, i) => i)
      .filter((i) => !side.onCourt.includes(i))
      .map(mapa),
  };
}

function finishMatch(match) {
  if (match.done) return;
  for (const side of [match.H, match.A]) {
    for (let i = 0; i < side.squad.length; i++) side.lines[i].seconds = side.state[i].seconds;
  }
  match.done = true;
}

/** Fecha a partida e devolve o resultado no formato de sempre. */
export function matchResult(match) {
  finishMatch(match);
  const { H, A, homeTeam, awayTeam } = match;
  return {
    seed: match.seed,
    overtimes: match.overtimes,
    home: {
      teamId: homeTeam.id,
      name: homeTeam.name,
      score: H.score,
      possessions: H.possessions,
      quarters: H.quarterScores,
      lines: H.lines,
      totals: teamTotals(H.lines),
    },
    away: {
      teamId: awayTeam.id,
      name: awayTeam.name,
      score: A.score,
      possessions: A.possessions,
      quarters: A.quarterScores,
      lines: A.lines,
      totals: teamTotals(A.lines),
    },
    winner: H.score > A.score ? homeTeam.id : awayTeam.id,
    log: match.log,
  };
}

/**
 * Simula a partida inteira de uma vez. E o caminho dos jogos que o usuario nao
 * assiste -- e continua sendo o mesmo motor, sem atalho nenhum.
 */
export function simulateGame(opts) {
  const match = createMatch(opts);
  stepMatch(match, { all: true });
  return matchResult(match);
}
